import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, loadToken, storeToken, type Engine, type Me, type Project } from './api.ts';
import { SignIn } from './pages/SignIn.tsx';
import { Projects } from './pages/Projects.tsx';
import { ProjectPage } from './pages/ProjectPage.tsx';
import { Setup } from './pages/Setup.tsx';
import { Users } from './pages/Users.tsx';
import { ChangePassword } from './ChangePassword.tsx';

/**
 * THE SHELL, and the four things this page can be:
 *
 *   no token      -> sign in
 *   checking      -> a token exists but we have not asked what it can do
 *   not an admin  -> a real account, wrong role. Said plainly, with a way out.
 *   ready         -> the tool
 *
 * "NOT AN ADMIN" IS A STATE, NOT AN ERROR TOAST. A reviewer who signs in here
 * has done nothing wrong and has a working account; what they need to be told
 * is that this particular page is not for them, and to be offered the door
 * back out. Failing the LOGIN instead would tell them their password was
 * wrong, which is a lie, and leaves "it works on my phone" unexplainable.
 *
 * THE SERVER DECIDES, NOT THIS PAGE. The role check that matters is
 * `requireAdmin` in `apps/server/src/routes/admin.ts`, on every request. This
 * component asks `GET /admin/engines` and treats a 403 as the answer — so the
 * page shows what the server would actually allow rather than a second, drifting
 * copy of the rule.
 *
 * PROJECTS ARE THE FRONT DOOR — the owner's restructure, executed as a
 * demolition and not a stacking: "I don't want you stacking things on top of
 * each other, I want you to clean up and recreate." The Upload, Recordings,
 * Versions and Player TABS are gone. Uploading is a section of a project's
 * page; the recording list IS the project's page; the player is a recording's
 * inner screen; the Versions tab's compare/promote already lived in the
 * player. What remains up top: Projects, Setup (the saved pipeline), Users.
 * The old header project picker died with the tabs — WHICH project you are in
 * is the URL now, so four pages can never disagree about it.
 */

/* ------------------------------------------------------------------ */
/* Where you are                                                       */
/* ------------------------------------------------------------------ */

const TABS = [
  { id: 'projects', label: 'Projects' },
  { id: 'setup', label: 'Setup' },
  { id: 'users', label: 'Users' },
] as const;

type TabId = (typeof TABS)[number]['id'];

interface Route {
  tab: TabId;
  /** `#/projects/<id>` — which project page is open, '' on the index. */
  projectId: string;
  /** `#/projects/<id>/r/<rid>` — which recording is open inside it. */
  recordingId: string;
  /** `…/r/<rid>/v/<vid|all>` — which lanes the player opens with. */
  versionId: string;
}

/**
 * NAVIGATION IN THE HASH, which is not a fashion choice: this bundle is
 * served from two places by one server (`/admin/` everywhere and the ROOT of
 * admin.taginai.com), and the hash never reaches the server, so one static
 * file answers every screen at both addresses. It also survives a reload and
 * can be bookmarked — which is exactly what a project's page deserves.
 *
 * The grammar: `#/projects`, `#/projects/<id>`, `#/projects/<id>/r/<rid>`,
 * `#/setup`, `#/users`. Anything else lands on the front door.
 */
function routeFromHash(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '').split('?')[0] ?? '';
  const parts = raw.split('/').filter(Boolean);
  const tab = TABS.find((t) => t.id === parts[0])?.id ?? 'projects';
  if (tab !== 'projects') return { tab, projectId: '', recordingId: '', versionId: '' };
  return {
    tab,
    projectId: parts[1] ?? '',
    recordingId: parts[2] === 'r' && parts[3] ? parts[3] : '',
    versionId: parts[4] === 'v' && parts[5] ? parts[5] : '',
  };
}

type Session =
  | { at: 'checking' }
  | { at: 'not-admin'; me: Me | null }
  | { at: 'ready'; me: Me | null; projects: Project[]; engines: Engine[] };

export function App(): JSX.Element {
  const [token, setToken] = useState<string | null>(() => loadToken());
  const [session, setSession] = useState<Session>({ at: 'checking' });
  const [fatal, setFatal] = useState<string | null>(null);
  const [route, setRoute] = useState<Route>(() => routeFromHash());
  const [changingPw, setChangingPw] = useState(false);

  // The back button is a real button. Without this listener it would change the
  // URL and leave the page showing the screen it was already on.
  useEffect(() => {
    const onHash = (): void => setRoute(routeFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const signOut = useCallback(() => {
    const dying = token;
    storeToken(null);
    setToken(null);
    setSession({ at: 'checking' });
    // Best effort, and deliberately not awaited. The token is already gone
    // from this browser; whether the server also hears about it changes
    // nothing the person can see.
    if (dying) void api.logout(dying).catch(() => {});
  }, [token]);

  const accept = useCallback((fresh: string) => {
    storeToken(fresh);
    setToken(fresh);
    setSession({ at: 'checking' });
  }, []);

  /** Re-ask for the project list — after a create, a rename, a Setup save. */
  const reloadProjects = useCallback(async () => {
    if (!token) return;
    try {
      const { projects } = await api.projects(token);
      const mine = projects.filter((p) => p.role === 'admin');
      setSession((s) => (s.at === 'ready' ? { ...s, projects: mine } : s));
    } catch {
      /* the next navigation refetches anyway */
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    void (async () => {
      try {
        /*
         * `/me` and `/projects` are allowed to fail without costing the page;
         * `/admin/engines` is the one that decides, so its 403 is the
         * not-an-admin answer.
         */
        const [engines, me, projects] = await Promise.all([
          api.engines(token),
          api.me(token).then(
            (r) => r.user,
            () => null,
          ),
          api.projects(token).then(
            (r) => r.projects,
            () => [] as Project[],
          ),
        ]);
        if (cancelled) return;

        // Only projects this account actually administers.
        const mine = projects.filter((p) => p.role === 'admin');
        setSession({ at: 'ready', me, projects: mine, engines: engines.engines });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiFailure && err.status === 403) {
          const me = await api.me(token).then(
            (r) => r.user,
            () => null,
          );
          if (!cancelled) setSession({ at: 'not-admin', me });
          return;
        }
        if (err instanceof ApiFailure && err.status === 401) {
          storeToken(null);
          setToken(null);
          return;
        }
        setFatal(
          err instanceof ApiFailure ? err.message : 'Could not reach the server. Is it running?',
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) return <SignIn onToken={accept} />;

  if (fatal) {
    return (
      <div className="signin">
        <div className="signin-card">
          <div className="signin-mark">
            <span className="wordmark">taginai</span>
            <span className="wordmark-sub">admin</span>
          </div>
          <div className="notice notice-error" role="alert">
            {fatal}
          </div>
          <div className="btn-row">
            <button className="btn-quiet" type="button" onClick={() => window.location.reload()}>
              Try again
            </button>
            <button className="btn-link" type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (session.at === 'checking') {
    return (
      <div className="signin">
        <div className="signin-card">
          <div className="signin-mark">
            <span className="wordmark">taginai</span>
            <span className="wordmark-sub">admin</span>
          </div>
          <p className="hint" style={{ margin: 0 }}>
            Checking your account…
          </p>
        </div>
      </div>
    );
  }

  if (session.at === 'not-admin') {
    return (
      <div className="signin">
        <div className="signin-card">
          <div className="signin-mark">
            <span className="wordmark">taginai</span>
            <span className="wordmark-sub">admin</span>
          </div>
          <div className="notice notice-warn" role="alert">
            <strong>This account is not an admin.</strong>
            <div style={{ marginTop: 4 }}>
              {session.me?.email ? `${session.me.email} signed in` : 'You are signed in'}, but no
              project lists you as an administrator. Ask an administrator to change your role,
              then sign in again.
            </div>
          </div>
          <button className="btn-quiet" type="button" onClick={signOut} style={{ width: '100%' }}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const { me, projects, engines } = session;
  const openProject = projects.find((p) => p.id === route.projectId);

  return (
    <div className="shell">
      <header className="topbar">
        <span className="wordmark">taginai</span>
        <span className="wordmark-sub">admin</span>

        <nav className="nav" aria-label="Sections">
          {TABS.map((t) => (
            <a
              key={t.id}
              className={`nav-tab${t.id === route.tab ? ' is-active' : ''}`}
              href={`#/${t.id}`}
              aria-current={t.id === route.tab ? 'page' : undefined}
            >
              {t.label}
            </a>
          ))}
        </nav>

        <span className="topbar-spacer" />
        <span className="topbar-who">
          <strong>{me?.displayName ?? 'Administrator'}</strong>
          {me?.email ?? ''}
        </span>
        <button className="btn-quiet" type="button" onClick={() => setChangingPw(true)}>
          Change password
        </button>
        <button className="btn-quiet" type="button" onClick={signOut}>
          Sign out
        </button>
      </header>

      {changingPw && token && <ChangePassword token={token} onClose={() => setChangingPw(false)} />}

      <main className="page stack">
        {route.tab === 'projects' && !route.projectId && (
          <Projects
            token={token}
            projects={projects}
            onDeleted={() => void reloadProjects()}
            onCreated={(id) => {
              void reloadProjects().then(() => {
                window.location.hash = `#/projects/${id}`;
              });
            }}
          />
        )}

        {route.tab === 'projects' && route.projectId && (
          <ProjectPage
            token={token}
            projectId={route.projectId}
            project={openProject ?? null}
            recordingId={route.recordingId}
            versionId={route.versionId}
            onProjectChanged={() => void reloadProjects()}
            onSignedOut={signOut}
          />
        )}

        {route.tab === 'setup' && (
          <Setup token={token} engines={engines} onSignedOut={signOut} />
        )}

        {route.tab === 'users' && (
          <Users token={token} meId={me?.id ?? null} onSignedOut={signOut} />
        )}
      </main>
    </div>
  );
}
